/**
 * Stripe Terminal — CAPACITOR adapter (2026-09-22). The staff app runs in TWO native shells:
 *   • the SwiftUI iPad app (WKWebView) → uses the hand-rolled `slikTerminal` bridge (terminalService.ts)
 *   • the Capacitor app (iOS + Android, incl. barbers' phones) → uses THIS, wrapping the
 *     @capacitor-community/stripe-terminal plugin (same native Stripe Terminal SDK, both platforms).
 *
 * This module normalizes the plugin's event-based API into the same request/response shape the
 * WKWebView bridge returns ({ok, ...}), so terminalService.ts can dual-path with one clean switch.
 * It is INERT unless running inside a real Capacitor app AND the reader toggle is on — so importing
 * it changes nothing for the SwiftUI iPad app (Capacitor.isNativePlatform() is false there).
 *
 * TESTING: `simulated:true` uses Stripe's simulated reader — full discover→connect→collect→charge
 * flow with NO hardware, on staging (test keys). Real M2 in test mode uses simulated:false + isTest.
 * BEHAVIOR-PIN: NATIVE-ARCHITECTURE-DECISION-2026-09-22.md · backup-register toggle.
 */
import { Capacitor } from '@capacitor/core';

type Result = { ok: boolean; error?: string; [k: string]: any };

// Lazy-load the plugin so the web/SwiftUI build never touches it. Typed loosely to avoid a hard
// dep in the type layer (the plugin's enums are imported by value only inside the native path).
let _plugin: any = null;
let _enums: { ConnectTypes: any; Events: any } | null = null;
let _initialized = false;
let _lastSimulated: boolean | undefined; // re-init when the simulated/real choice changes
let _lastReaders: any[] = [];
let _tokenListenerAttached = false;
let _updateListenersAttached = false;
// WARM CONNECTION (Jose 2026-10-23): the serial we currently believe we're connected to. Set on a
// successful connect, cleared on disconnect / a failed collect. Lets the app pre-connect the reader
// on open and lets a charge SKIP discover+connect when it's already connected (fast, no "looking…").
let _connectedSerial: string | null = null;
let _disconnectListenerAttached = false;
export function capIsConnected(serial?: string): boolean {
  return !!_connectedSerial && (!serial || _connectedSerial === serial);
}

// A status sink so firmware-update progress reaches the checkout UI. Without this, a first-connect
// M2 firmware update (minutes long) shows only a blank "connecting" spinner and looks hung. The
// checkout flow sets this before connecting; the plugin's update events push human text through it.
let _statusHook: ((msg: string) => void) | null = null;
export function setCapStatus(fn: ((msg: string) => void) | null): void { _statusHook = fn; }
const say = (m: string) => { try { _statusHook?.(m); } catch { /* noop */ } };

/** True only inside a real Capacitor app (iOS/Android) — false in the SwiftUI WKWebView + plain web. */
export function capacitorNative(): boolean {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

async function loadPlugin(): Promise<boolean> {
  if (_plugin) return true;
  if (!capacitorNative()) return false;
  try {
    const mod: any = await import('@capacitor-community/stripe-terminal');
    _plugin = mod.StripeTerminal;
    _enums = { ConnectTypes: mod.TerminalConnectTypes, Events: mod.TerminalEventsEnum };
    return !!_plugin;
  } catch { return false; }
}

/**
 * Stripe Terminal REQUIRES Location "When In Use" authorization, and the community plugin does NOT
 * request it (the SwiftUI app did, via CLLocationManager). Running discovery with location
 * un-granted crashes the SDK natively. Trigger the app's location prompt from the WebView's
 * geolocation (uses the app's Info.plist NSLocationWhenInUseUsageDescription) BEFORE any discovery,
 * so authorization is set first. Best-effort + timeout — never blocks forever. (Fix 2026-09-22)
 */
function ensureLocationPermission(): Promise<void> {
  return new Promise((resolve) => {
    try {
      const geo = (navigator as any)?.geolocation;
      if (!geo?.getCurrentPosition) return resolve();
      geo.getCurrentPosition(() => resolve(), () => resolve(), { timeout: 10000, maximumAge: 60000 });
    } catch { resolve(); }
  });
}

/**
 * Idempotent init: request location, attach the connection-token provider, initialize the SDK.
 * `simulated` maps to the plugin's `isTest` (which drives BluetoothScan setSimulated) — this is
 * DECOUPLED from the Stripe key mode (test vs live is decided server-side by the connectionToken
 * function). So on staging with test keys: simulated=true → a virtual reader (no hardware);
 * simulated=false → the REAL M2 over Bluetooth, still in test mode. On prod, simulated is always
 * false → real M2, live keys. Re-initializes when the simulated choice flips (initialize updates
 * isTest each call; the plugin guards its one-time SDK setup internally). (2026-09-22)
 */
async function ensureInit(fetchToken: () => Promise<string>, simulated: boolean): Promise<void> {
  if (!(await loadPlugin())) throw new Error('Terminal plugin unavailable');
  await ensureLocationPermission(); // MUST precede any Terminal SDK discovery or it crashes natively
  if (!_tokenListenerAttached) {
    // The SDK fires RequestedConnectionToken whenever it needs a fresh token; we answer with our
    // server-minted secret. Same connectionToken Cloud Function the iPad + web already use. If the
    // fetch fails we DON'T set an empty token (that trips the SDK) — we surface a clear error path.
    await _plugin.addListener(_enums!.Events.RequestedConnectionToken, async () => {
      try {
        const token = await fetchToken();
        if (token) await _plugin.setConnectionToken({ token });
      } catch { /* leave unset — discovery/connect will reject with a real error, not crash */ }
    });
    _tokenListenerAttached = true;
  }
  if (!_updateListenersAttached) {
    // Firmware updates: a required update installs automatically during connect (esp. the M2's first
    // connect in a new mode) and can take minutes. Surface it so the UI isn't a silent spinner.
    // Progress is a 0..1 float. DON'T let the reader be powered off / the app reload mid-update.
    await _plugin.addListener(_enums!.Events.StartInstallingUpdate, () =>
      say('Updating the reader’s software — keep it powered and nearby. This can take a few minutes…'));
    await _plugin.addListener(_enums!.Events.ReaderSoftwareUpdateProgress, (ev: any) => {
      const pct = Math.round(Math.max(0, Math.min(1, Number(ev?.progress) || 0)) * 100);
      say(`Updating reader… ${pct}% (don’t close the app or power off the reader)`);
    });
    await _plugin.addListener(_enums!.Events.FinishInstallingUpdate, () =>
      say('Reader updated — finishing connection…'));
    _updateListenersAttached = true;
  }
  if (!_disconnectListenerAttached) {
    // When the reader drops (idle BT power-save, powered off, out of range) clear our warm-connection
    // flag so the next charge falls back to a fresh discover+connect instead of trusting a dead link.
    // Event name varies by plugin version — try the known ones, guarded so an unknown name can't throw.
    for (const name of ['DisconnectedReader', 'ReaderDisconnected', 'ReaderReconnectFailed', 'UnexpectedReaderDisconnect']) {
      try {
        const ev = (_enums!.Events as any)?.[name];
        if (ev) await _plugin.addListener(ev, () => { _connectedSerial = null; });
      } catch { /* unknown event on this plugin version — ignore */ }
    }
    _disconnectListenerAttached = true;
  }
  if (!_initialized || _lastSimulated !== simulated) {
    await _plugin.initialize({ isTest: simulated });
    _initialized = true;
    _lastSimulated = simulated;
  }
}

/** Discover readers. `simulated` → Stripe's simulated reader (no hardware); else Bluetooth (M2). */
export async function capDiscover(
  fetchToken: () => Promise<string>,
  opts: { simulated: boolean; locationId?: string; timeoutMs?: number },
): Promise<Result> {
  try {
    await ensureInit(fetchToken, opts.simulated);
    // ALWAYS Bluetooth (the M2's transport). The plugin's discover only supports TapToPay/Internet/
    // Bluetooth — a "Simulated" type errors ("not support now"). Simulation is driven by isTest at
    // initialize() → BluetoothScanDiscoveryConfigurationBuilder().setSimulated(isTest), so on staging
    // (isTest=true) the Bluetooth scan returns a SIMULATED reader with no hardware. (Fix 2026-09-22)
    const type = _enums!.ConnectTypes.Bluetooth;
    _lastReaders = [];
    return await new Promise<Result>(async (resolve) => {
      let settled = false;
      const done = (r: Result) => { if (!settled) { settled = true; try { handle?.remove(); } catch {} resolve(r); } };
      const timer = setTimeout(() => done({ ok: false, error: 'Reader discovery timed out' }), opts.timeoutMs ?? 30000);
      const handle = await _plugin.addListener(_enums!.Events.DiscoveredReaders, (ev: any) => {
        const readers = ev?.readers || [];
        if (readers.length) {
          _lastReaders = readers;
          clearTimeout(timer);
          done({ ok: true, readers: readers.map((x: any) => ({ serial: x.serialNumber, label: x.label || x.serialNumber, battery: x.batteryLevel })) });
        }
      });
      try {
        await _plugin.discoverReaders({ type, locationId: opts.locationId });
      } catch (e: any) { clearTimeout(timer); done({ ok: false, error: e?.message || 'discover failed' }); }
    });
  } catch (e: any) { return { ok: false, error: e?.message || 'discover failed' }; }
}

/** Connect to a discovered reader by serial (matched from the last discovery result).
 *  GUARDS every value the plugin force-unwraps (reader/serialNumber/locationId) so a bad input
 *  fails with a clear message instead of a native nil-crash. */
export async function capConnect(serial: string, locationId: string, _simulated?: boolean): Promise<Result> {
  try {
    if (!_plugin) return { ok: false, error: 'Reader not initialized' };
    // Already connected to this reader → reuse it (warm connection). No native reconnect needed.
    if (_connectedSerial && _connectedSerial === serial) return { ok: true, serial };
    const reader = _lastReaders.find((r) => r.serialNumber === serial) || _lastReaders[0];
    if (!reader || !reader.serialNumber) return { ok: false, error: 'No discovered reader to connect' };
    if (!locationId) return { ok: false, error: 'Missing Stripe Location for the reader' }; // plugin force-unwraps this
    const type = _enums!.ConnectTypes.Bluetooth; // always BT (simulated is driven by isTest, not type)
    try {
      await _plugin.connectReader({ reader, type, locationId });
    } catch (e: any) {
      // The SDK rejects a second connect to an already-connected reader — that's success for us
      // (a warm connection from app-open), not a failure. Any other error clears the flag + propagates.
      if (/already\s*connect/i.test(e?.message || '')) { _connectedSerial = reader.serialNumber; return { ok: true, serial: reader.serialNumber }; }
      _connectedSerial = null;
      return { ok: false, error: e?.message || 'connect failed' };
    }
    _connectedSerial = reader.serialNumber;
    return { ok: true, serial: reader.serialNumber };
  } catch (e: any) { _connectedSerial = null; return { ok: false, error: e?.message || 'connect failed' }; }
}

/** Collect + confirm on the connected reader. PI id comes from the server; we just need success.
 *  GUARDS clientSecret (the plugin force-unwraps it) so an empty secret can't nil-crash. */
export async function capCollect(clientSecret: string, paymentIntentId?: string): Promise<Result> {
  try {
    if (!_plugin) return { ok: false, error: 'Reader not initialized' };
    if (!clientSecret) return { ok: false, error: 'Missing payment (no client secret)' }; // plugin force-unwraps this
    await _plugin.collectPaymentMethod({ paymentIntent: clientSecret });
    // The card was just presented + READ (the reader beeped) — collectPaymentMethod resolves on the tap.
    // The charge is NOT done yet; confirmPaymentIntent authorizes it now. Flip the on-screen state off
    // "Tap your card" → "Processing…" THE INSTANT the card is read, so the client removes the card and
    // sees progress instead of staring at "tap" while it silently works. (Jose 2026-10-21, #132)
    say('Processing…');
    // AUTO-RETRY ON NETWORK BLIP (Jose 2026-10-30): the card is already READ here, so a transient NETWORK
    // error on confirm (weak WiFi — the real cause of Alan Reid's Amex) is safe to retry WITHOUT re-tapping:
    // confirmPaymentIntent on the SAME collected PI completes the pending auth or no-ops if it already went
    // through — it never double-charges (no re-collect). Retry ONCE, and ONLY on a network error (never on a
    // card decline — retrying a declined card is pointless).
    try {
      await _plugin.confirmPaymentIntent();
    } catch (ce: any) {
      const m = String(ce?.message || ce?.code || '').toLowerCase();
      // Retry ONLY on a true transport/connection failure (the request never reached Stripe). NEVER on a
      // card/processing error — e.g. Amex `processing_error` ("…couldn't be processed. Try the payment
      // again.") is a card-side failure that retrying just repeats (confirmed: 4 Amex retries all failed).
      const looksConnection = /unable to connect|no internet|connection (lost|failed|error|was lost)|network error|timed ?out|timeout|unreachable|api_connection/.test(m);
      const looksCardError = /processed|declin|processing_error|insufficient|card_error|do_not_honor/.test(m);
      if (!looksConnection || looksCardError) throw ce;
      say('Network hiccup — retrying…');
      await new Promise((r) => setTimeout(r, 1500));
      try {
        await _plugin.confirmPaymentIntent();
      } catch (ce2: any) {
        const m2 = String(ce2?.message || ce2?.code || '').toLowerCase();
        if (/already|succeeded|complete/.test(m2)) return { ok: true, paymentIntentId }; // it DID go through
        throw ce2;
      }
    }
    return { ok: true, paymentIntentId };
  } catch (e: any) {
    // A collect failure may mean the connection dropped — forget the warm connection so the NEXT
    // attempt does a clean discover+connect instead of trusting a possibly-dead link (self-heal).
    _connectedSerial = null;
    return { ok: false, error: e?.message || 'payment not completed' };
  }
}

/** Best-effort cancel of any in-flight discovery/collect. */
export async function capCancel(): Promise<void> {
  try { await _plugin?.cancelDiscoverReaders?.(); } catch { /* */ }
  try { await _plugin?.cancelCollectPaymentMethod?.(); } catch { /* */ }
}
